import { motion } from 'framer-motion';
import { Button } from '@educa-legal/ui';

const riskAreas = [
  'Atendimento a alunos com necessidades de inclusão',
  'Conflitos entre pais, alunos e colaboradores',
  'Pagamento correto de impostos e obrigações fiscais',
  'Novas legislações educacionais que impactam diretamente sua operação',
];

const verticalLinesPattern = {
  backgroundImage:
    'repeating-linear-gradient(90deg, #D4512A 0, #D4512A 1px, transparent 1px, transparent 96px)',
};

export function PainPoints() {
  return (
    <section
      id="dores"
      className="relative py-24 md:py-32 bg-neutral-950 overflow-hidden"
    >
      {/* Pattern editorial sutil — mesma DNA do Hero, em opacidade mais alta sobre o dark */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={verticalLinesPattern}
      />
      {/* Linha horizontal de fim de seção */}
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-px bg-neutral-800"
      />

      <div className="section-container relative">
        {/* Eyebrow editorial */}
        <motion.div
          className="flex items-center gap-3 mb-12 md:mb-16"
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-64px' }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <motion.span
            className="h-px bg-orange origin-left"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
            style={{ width: 40 }}
          />
          <span className="text-caption font-semibold uppercase tracking-[0.22em] text-neutral-500">
            Áreas de Risco
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Esquerda — argumento editorial */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-48px' }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h2 className="text-display-md md:text-display-lg font-bold text-white leading-[1.1] tracking-tight">
              Você não está devidamente assessorado — e isso está onerando a operação da sua escola.
            </h2>

            <p className="mt-7 text-body-md md:text-body-lg text-neutral-400 leading-relaxed max-w-xl">
              Gerir uma escola sem o jurídico certo custa mais do que você imagina. Sem a equipe
              especializada ao seu lado, temas do dia a dia deixam de receber o tratamento adequado
              — e riscos jurídicos reais passam a ser corridos em áreas como as listadas ao lado.
            </p>

            {/* Fechamento — blockquote com border laranja (o "antídoto") */}
            <blockquote className="mt-8 border-l-2 border-orange pl-5 max-w-xl">
              <p className="text-body-md text-neutral-200 leading-relaxed">
                Contar com especialistas no setor reduz o desgaste diário dos mantenedores, diminui
                os custos com resolução de problemas e pagamento de impostos e simplifica a gestão
                jurídica da sua instituição.
              </p>
            </blockquote>

            <div className="mt-10">
              <Button asChild variant="primary" size="lg">
                <a href="#servicos">Conheça os serviços da Educa Legal</a>
              </Button>
            </div>
          </motion.div>

          {/* Direita — tabela editorial das 4 áreas */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-48px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ul
              role="list"
              className="border-t border-b border-neutral-800 divide-y divide-neutral-800"
            >
              {riskAreas.map((area, i) => (
                <motion.li
                  key={area}
                  className="group relative flex items-start gap-6 md:gap-8 py-6 md:py-7 transition-colors duration-200 hover:bg-neutral-900/50"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-32px' }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
                >
                  {/* Numeração editorial */}
                  <span className="flex-shrink-0 w-12 text-display-sm md:text-display-md font-bold text-orange tabular-nums leading-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>

                  <div className="flex-1 pt-1">
                    <span
                      aria-hidden
                      className="block h-px w-8 bg-orange/50 mb-3 transition-all duration-200 group-hover:w-12 group-hover:bg-orange"
                    />
                    <p className="text-body-md md:text-body-lg text-neutral-100 leading-relaxed">
                      {area}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
